import React, { useState } from 'react';
import { CheckCircle, Award, BookOpen, Heart, Play, MapPin, Mail, Phone, Globe, Instagram, Youtube } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Custom TikTok SVG component
const TikTok = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    fill="currentColor"
    className={className}
    style={{ transform: 'translate(-2px, -2px)' }}
  >
    <path d="M28.5 10.5c-2.5 0-4.5-2-4.5-4.5h-4v18c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4c.3 0 .7 0 1 .1v-4.1c-.3 0-.7-.1-1-.1-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8V14.5c1.2 1 2.7 1.5 4.5 1.5v-5.5z" />
  </svg>
);

const About = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />

      <main className="flex-grow">
      

        {/* About Coach HT Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                About <span className="text-primary-500">Coach HT</span>
              </h2>
            </div>

            <div className="flex flex-col md:flex-row gap-12 items-start">
              {/* Coach HT image and contact info */}
              <div className="w-full md:w-2/5">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-primary-200 rounded-full opacity-20 transform scale-110 blur-lg"></div>
                  <img
                    src="/coach_ht.png"
                    alt="Coach HT"
                    className="relative z-10 rounded-3xl shadow-lg w-full"
                  />
                </div>

                {/* Contact Information */}
                <div className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-2xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Mail size={20} className="text-primary-500" />
                    Contact Information
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Location */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <MapPin size={16} className="text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="text-gray-900 font-medium">Los Angeles, CA</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Mail size={16} className="text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <a 
                          href="mailto:phytgymla@gmail.com" 
                          className="text-gray-900 font-medium hover:text-primary-600 transition-colors"
                        >
                          phytgymla@gmail.com
                        </a>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Phone size={16} className="text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <a 
                          href="tel:4242551338" 
                          className="text-gray-900 font-medium hover:text-primary-600 transition-colors"
                        >
                          (424) 255-1338
                        </a>
                      </div>
                    </div>

                    {/* Website */}
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Globe size={16} className="text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Website</p>
                        <a 
                          href="https://www.thephytgym.com/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-900 font-medium hover:text-primary-600 transition-colors"
                        >
                          thephytgym.com
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Social Media Links */}
                  <div className="mt-6 pt-6 border-t border-primary-100">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Follow Coach HT</h4>
                    <div className="flex gap-3">
                      <a 
                        href="https://instagram.com/thephytgym" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg flex items-center justify-center text-white hover:scale-110 transition-transform shadow-md"
                      >
                        <Instagram size={18} />
                      </a>
                      <a 
                        href="https://youtube.com/@thephytgym" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center text-white hover:scale-110 transition-transform shadow-md"
                      >
                        <Youtube size={18} />
                      </a>
                      <a 
                        href="https://tiktok.com/@thephytgym" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gradient-to-r from-black to-gray-800 rounded-lg flex items-center justify-center text-white hover:scale-110 transition-transform shadow-md"
                      >
                        <TikTok className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coach HT story */}
              <div className="w-full md:w-3/5">
                <h3 className="text-2xl font-bold mb-4 text-primary-500">My Story</h3>
                
                <div className="text-gray-700 space-y-6">
                  <p>
                    My name is Heather, you can call me HT for short, HT Physique for long! Originally a Denver native, I grew up in a dancing family. My mother owned her own ballet studio, and so did my grandmother. I was trained in the Cecchetti method of teaching ballet, which has been developing since 1918, and which focuses on proper form and alignment. It was my ballet training that made me fall in love with movement.
                  </p>
                  
                  <p>
                    After graduating from Loyola Marymount University (LMU) in 2012, I decided to make Los Angeles my permanent home. In 2019 I competed in Summer Shredding, a bikini bodybuilding competition.
                  </p>
                  
                  <p>
                    In the three months leading up to the competition, I worked out six days a week, tracking every single calorie in and out. Sounds horrible, I know, but in three months I was able to drop over ten percent in body fat, which translated to a loss of about 20 pounds. That was mind blowing to me because it proved that if I just "trust the process," the process works.
                  </p>
                  
                  <div className="bg-gradient-to-r from-primary-50 to-purple-50 p-6 rounded-xl border-l-4 border-primary-500">
                    <h4 className="text-lg font-bold text-primary-600 mb-3">The PHYT Gym Journey</h4>
                    <p className="text-gray-700">
                      Soon after the bodybuilding competition, I made a full-time commitment to changing people's lives through health and fitness. I "fired my boss" as they say, and became a full time Fitness Professional. Three months later the pandemic hit, and everything changed. I embarked on my "pandemic project," The PHYT Gym. I converted my old, scary looking garage into a cute, hip, workout space for women.
                    </p>
                    <p className="text-gray-700 mt-3">
                      When things opened back up, I found myself energized and ready to help others become the best version of themselves. However, I found that with all my experience, and my certifications, I was still missing something. I still was unable to fully help my female clientele, particularly those who were present or future mothers. Shortly after this realization I decided to reinvest in myself and obtain my Pre and Post Natal Certification.
                    </p>
                  </div>
                  
                  <div className="bg-primary-500 p-6 rounded-xl text-white">
                    <h4 className="text-lg font-bold mb-3">The PHYT Method Philosophy</h4>
                    <p>
                      I strive to make women feel better and look better from the outside in, as well as the inside out. The PHYT Method is not just about working out efficiently with good form. It is about becoming a better, happier, more confident individual through positive habit formation and lifestyle change discussion and support. It is about making realistic changes that produce real results. My job is to get you where you want to be faster.
                    </p>
                  </div>
                </div>
                
                <div className="mt-8 bg-primary-50 p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-bold mb-3 text-primary-500">My Mission</h3>
                  <p className="text-gray-700">
                    To help you achieve your health and fitness goals through the PHYT method - a comprehensive approach that combines proper form, alignment, nutrition, and personalized training to get you results faster than traditional methods.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Fun Facts Section */}
        <section className="py-16 bg-primary-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                Fun Facts About <span className="text-primary-500">Coach HT</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition duration-300">
                <div className="text-4xl mb-4">🩰</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Ballet Trained</h3>
                <p className="text-gray-700">Denver, CO native trained in the Cecchetti method of ballet</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition duration-300">
                <div className="text-4xl mb-4">📑</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Psychology BA</h3>
                <p className="text-gray-700">Graduated from LMU in 2012 with a degree in Psychology</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition duration-300">
                <div className="text-4xl mb-4">👙</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Bikini Competitor</h3>
                <p className="text-gray-700">Former bikini bodybuilding competitor with competition experience</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition duration-300">
                <div className="text-4xl mb-4">🍋🧀🍌</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">NASM Certified</h3>
                <p className="text-gray-700">NASM Certified Personal Trainer + Nutrition Certification</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition duration-300">
                <div className="text-4xl mb-4">💪</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Pre/Post Natal</h3>
                <p className="text-gray-700">Pre and Post Natal Certified Trainer for all stages of motherhood</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition duration-300">
                <div className="text-4xl mb-4">🏆</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Founder</h3>
                <p className="text-gray-700">Founder of The PHYT Gym and creator of the PHYT method</p>
              </div>
            </div>
          </div>
        </section>

        {/* What Makes Us Different Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                What Makes <span className="text-primary-500">THE PHYT GYM</span> Different
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our proven programs are designed to get you results faster than traditional methods
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Feature 1 */}
              <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition duration-300 border border-primary-100">
                <div className="text-primary-500 mb-4">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">PHYT Method</h3>
                <p className="text-gray-700">
                  A method focused on proper form and alignment that gets you results twice as fast
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition duration-300 border border-primary-100">
                <div className="text-primary-500 mb-4">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Proven Results</h3>
                <p className="text-gray-700">
                  500+ clients trained with a 95% success rate and 1000+ transformations
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition duration-300 border border-primary-100">
                <div className="text-primary-500 mb-4">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">8+ Years Experience</h3>
                <p className="text-gray-700">
                  Extensive experience helping clients reach their health and fitness goals
                </p>
              </div>
            </div>

            <div className="bg-primary-500 rounded-xl p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">What We Focus On</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1 text-primary-200">✓</div>
                      <p>Proper Form & Alignment - Ensuring safe and effective workouts</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1 text-primary-200">✓</div>
                      <p>Nutrition Coaching - Comprehensive meal planning and guidance</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1 text-primary-200">✓</div>
                      <p>Personalized Training - Customized programs for your specific goals</p>
                    </li>
                  </ul>
                </div>
                <div className="flex justify-center">
                  <a
                    href="/#packages"
                    className="bg-white text-primary-500 font-bold text-lg px-8 py-4 rounded-xl hover:bg-gray-100 transition duration-300 shadow-lg"
                  >
                    View Programs
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Video Section */}
        <section className="py-16 bg-primary-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                Featured <span className="text-primary-500">Video</span>
              </h2>
            </div>

            <div className="flex justify-center">
              <div className="w-full max-w-4xl">
                <div className="relative">
                  {!videoPlaying ? (
                    <>
                      <div className="absolute inset-0 bg-primary-200 rounded-3xl opacity-20 transform scale-110 blur-lg"></div>
                      <img
                        src="https://img.youtube.com/vi/GR0ZSQjJ1Tc/maxresdefault.jpg"
                        alt="Coach HT Video Thumbnail"
                        className="relative z-10 rounded-3xl shadow-lg w-full object-cover"
                        onError={(e) => {
                          e.target.src = "/coach.png";
                        }}
                      />
                      {/* Play button overlay */}
                      <button 
                        onClick={() => setVideoPlaying(true)}
                        className="absolute inset-0 z-20 flex items-center justify-center bg-black bg-opacity-30 rounded-3xl transition-opacity hover:bg-opacity-40"
                      >
                        <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center">
                          <Play size={32} className="text-white ml-1" />
                        </div>
                        <span className="absolute bottom-6 text-white font-medium bg-black bg-opacity-50 px-4 py-2 rounded-full">
                          Watch Coach HT in Action
                        </span>
                      </button>
                    </>
                  ) : (
                    <div className="rounded-3xl overflow-hidden shadow-lg relative pt-[56.25%] w-full">
                      <iframe 
                        className="absolute top-0 left-0 w-full h-full"
                        src="https://www.youtube.com/embed/GR0ZSQjJ1Tc?autoplay=1&rel=0&modestbranding=1"
                        // src="https://www.youtube.com/watch?v=GR0ZSQjJ1Tc&ab_channel=ThePHYTGym"
                        title="Coach HT Introduction"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  )}
                </div>
                
                {/* Video toggle button when video is playing */}
                {videoPlaying && (
                  <button 
                    onClick={() => setVideoPlaying(false)}
                    className="mt-4 text-primary-500 font-medium flex items-center justify-center gap-2 w-full hover:text-primary-600"
                  >
                    Show Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About; 